import * as v from 'valibot';

/**
 * Slack as reef's second front door.
 *
 * These are the small vocabulary the surface needs and the studio has to render
 * too — where a review goes, and who wants it personally. They live in the
 * schema package rather than in the Worker because the builder shows and edits
 * the same bindings, and a channel picker that disagreed with the router about
 * what `dm_only` means would be a settings screen that quietly does nothing.
 */

/**
 * What reef posts into a channel.
 *
 * Split because the audiences are different. A reviewer wants only what is
 * waiting on them; an operator wants to see runs start and finish. Giving both
 * to either is how a channel gets muted, and a muted channel is worse than no
 * channel — it looks configured.
 */
export const SlackChannelPurpose = v.picklist(['approvals', 'runs', 'all']);
export type SlackChannelPurpose = v.InferOutput<typeof SlackChannelPurpose>;

/**
 * How one person wants to be told about a review.
 *
 * `dm` is additive — the channel still gets its copy, and the reviewer also
 * gets one they cannot miss. `dm_only` is the setting somebody in forty
 * channels asks for, and it deliberately does not suppress anybody else's copy:
 * a preference that could silence a channel for everyone would be a preference
 * that loses approvals.
 */
export const SlackReviewRoute = v.picklist(['channel', 'dm', 'dm_only']);
export type SlackReviewRoute = v.InferOutput<typeof SlackReviewRoute>;

/** The things reef keeps a Slack thread for. */
export const SlackThreadSubject = v.picklist(['run', 'approval']);
export type SlackThreadSubject = v.InferOutput<typeof SlackThreadSubject>;

export const SlackChannelBinding = v.object({
  id: v.string(),
  teamId: v.string(),
  channelId: v.string(),
  channelName: v.nullable(v.string()),
  /** `null` binds every workflow; an id narrows it to one. */
  workflowId: v.nullable(v.string()),
  purpose: SlackChannelPurpose,
  createdBy: v.string(),
  createdAt: v.string(),
});
export type SlackChannelBinding = v.InferOutput<typeof SlackChannelBinding>;

export const SlackPreference = v.object({
  userId: v.string(),
  reviewRoute: SlackReviewRoute,
  slackUserId: v.nullable(v.string()),
});
export type SlackPreference = v.InferOutput<typeof SlackPreference>;
